import type { FC } from 'react';
import { useContext, useState } from 'react';
import classNames from 'classnames';

import Globe from 'app/icons/globe.svg';
import ConfigContext from 'app/common/ConfigContext';
import Dropdown from 'app/components/Dropdown';
import CountryFlag from 'app/components/CountryFlag';
import { languageNameHelper } from 'app/i18n/utils';
import { findIndexOrNull } from 'app/utils/array';
import BlockTemplate from 'app/components/Blocks/BlockTemplate';

import {
  getLanguageRegion,
  getStreamName,
  getStreamType,
  getStreamTypeIcon,
  getStreamUrl
} from './utils';
import style from './style.module.scss';

interface Props {
  streams: Stream[];
  videogameSlug?: VideogameSlug;
}

const StreamBlock: FC<Props> = ({ streams, videogameSlug }) => {
  const { multi_lang, lang, origin, parents } = useContext(ConfigContext);

  // A stream without a player URL (Douyu/TikTok, a YouTube or Facebook channel URL) is skipped:
  // auto-playing it would show the no-stream error and hide the language dropdown.
  const usableStreams = streams.filter(
    stream =>
      getStreamType(stream, videogameSlug) && getStreamUrl(stream, origin, parents, videogameSlug)
  );
  const autoPlayedStream: number | undefined = usableStreams.length
    ? findIndexOrNull(usableStreams, stream => lang.toLowerCase().includes(stream.language)) ?? // find the stream with the corresponding language
      findIndexOrNull(usableStreams, stream => stream.main) ?? // else take the main one
      findIndexOrNull(usableStreams, stream => stream.official) ?? // else take the official one
      0 // else take the first one
    : undefined; // if not usable streams, returns undefined

  const languageName = languageNameHelper(lang);

  const [streamIndex, setStreamIndex] = useState(autoPlayedStream);

  const stream: Stream | undefined = usableStreams.find((_, index) => index === streamIndex)!;
  const streamUrl = getStreamUrl(stream, origin, parents, videogameSlug);

  return (
    <BlockTemplate
      hasData={Boolean(usableStreams.length) && !!streamUrl}
      errorType="no-stream"
      className={classNames(style.streamBlockTemplate, 'override-block_stream')}
      containerClassname={classNames(style.streamBlock, 'override-block_stream-content')}
    >
      {multi_lang && usableStreams.length && (
        <Dropdown
          onChange={value => setStreamIndex(parseInt(value))}
          value={streamIndex?.toString()}
          options={usableStreams.map((stream, index) => {
            const Icon = getStreamTypeIcon(getStreamType(stream, videogameSlug));
            const name = getStreamName(stream, videogameSlug);
            return {
              label: (
                <>
                  <CountryFlag country={getLanguageRegion(stream.language)} />{' '}
                  {Icon && <Icon className={style.flag} />} {languageName.of(stream.language)}
                  {name ? ` - ${name}` : ''}
                </>
              ),
              value: index.toString()
            };
          })}
          label={
            <span className={style.dropdownLabel}>
              <Globe />
              {languageName.of(stream.language)}
            </span>
          }
          position="top"
          withArrow={false}
          className={style.languageSelect}
          arrowIconClassName={style.dropdownArrow}
          contentClassName={style.dropdown}
          openedStyle={{ opacity: 1 }}
          optionClassName={style.langOption}
          alignment="start"
        />
      )}

      <iframe
        src={streamUrl!}
        height="100%"
        width="100%"
        title="Video player"
        frameBorder="0"
        allowFullScreen
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      />

      {/* Maybe add a condition here, like knowing if the match offers microbet */}
      {/* <MicrobettingContainer matchId={matchId} /> */}
    </BlockTemplate>
  );
};

StreamBlock.displayName = 'StreamBlock';

export default StreamBlock;
